import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import { useTranslation } from "next-i18next";

const industries = [
  {
    slug: "transport",
    title: "Transportation & Infrastructure",
    subtitle: "Airports · Highways · Logistics Hubs",
    description: "Real-time monitoring for airports, traffic flow optimization, and incident detection",
    backgroundImage: "/images/menu/transportation.png",
    color: "transport-mint",
    href: "/solutions/transportation"
  },
  {
    slug: "retail",
    title: "Retail & Commercial Spaces",
    subtitle: "Stores, shopping centers, and outlets",
    description: "Smart customer insights and loss prevention",
    backgroundImage: "/images/industries/retail.jpg",
    color: "retail-coral",
    href: "/solutions/retail"
  },
  {
    slug: "industry",
    title: "Industrial & Manufacturing",
    subtitle: "Factories, warehouses, and production facilities",
    description: "Automated safety monitoring and compliance tracking",
    backgroundImage: "/images/industries/ppe.png",
    color: "industry-lilac",
    href: "/solutions/industries"
  }
];

const IndustryShowcase = () => {
  const { t } = useTranslation('home');
  const router = useRouter();

  const handleCardClick = (href: string) => {
    router.push(href);
  };

  return (
    <motion.section
      className="pt-20 md:pt-24"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true, margin: "-50px" }}
    >
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            {t('industries.title')}
          </h2>
          <p className="text-xl md:text-2xl text-secondary-foreground max-w-4xl mx-auto leading-relaxed">
            {t('industries.subtitle')}
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {industries.map((industry, index) => (
            <motion.div
              key={industry.slug}
              onClick={() => handleCardClick(industry.href)}
              className="group relative h-96 md:h-[28rem] rounded-2xl overflow-hidden cursor-pointer light_effect_card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              viewport={{ once: true }}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <Image
                  src={industry.backgroundImage}
                  alt={industry.title}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>

              {/* Gradient Overlay */}
              <div
                className="absolute inset-0 transition-all duration-500"
                style={{
                  background: `linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 60%, hsl(var(--${industry.color}) / 0.7) 100%)`
                }}
              />

              {/* Hover Gradient Overlay */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500"
                style={{
                  background: `linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 30%, hsl(var(--${industry.color}) / 0.8) 100%)`
                }}
              />

              {/* Content */}
              <div className="relative h-full flex flex-col justify-end p-8 text-white">
                <div className="space-y-3">
                  <h3 className="text-2xl md:text-3xl font-bold leading-tight">
                    {t(`industries.${industry.slug}.title`)}
                  </h3>
                  <p className="text-white/90 text-base md:text-lg">
                    {t(`industries.${industry.slug}.subtitle`)}
                  </p>
                </div>

                {/* CTA */}
                <div className="flex items-center gap-2 mt-6 text-white group-hover:translate-x-1 transition-transform">
                  <span className="font-medium">{t('industries.cta', { defaultValue: 'Explore' })}</span>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Microcopy Bridge */}
        <motion.div
          className="text-center mt-16 md:mt-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-lg md:text-xl font-semibold text-primary-foreground mb-2">
            {t('industries.bridge.question', { defaultValue: 'Not sure where to start?' })}
          </p>
          <p className="text-base md:text-lg text-secondary-foreground max-w-3xl mx-auto">
            {t('industries.bridge.description', { defaultValue: 'Discover how AI transforms real-world operations, from visibility to execution.' })}
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default IndustryShowcase;